package com.essntl.core.settings.data.repository

import com.essntl.core.settings.data.supabase.configuration.ConfigurationCurrencyDto
import com.essntl.core.settings.data.supabase.configuration.ConfigurationEmailDto
import com.essntl.core.settings.data.supabase.configuration.ConfigurationLogoDto
import com.essntl.core.settings.data.supabase.configuration.ConfigurationSupabaseDataSource
import com.essntl.core.settings.data.supabase.configuration.toRequestMap
import com.essntl.core.settings.domain.model.configuration.ConfigurationCurrencyModel
import com.essntl.core.settings.domain.model.configuration.ConfigurationEmailModel
import com.essntl.core.settings.domain.model.configuration.ConfigurationLogoModel
import com.essntl.core.settings.domain.model.configuration.ConfigurationModel
import com.essntl.core.settings.domain.repository.ConfigurationRepository
import com.essntl.core.supabase.SupabaseFile
import com.essntl.core.supabase.deleteImage
import com.essntl.core.supabase.getAll
import com.essntl.core.supabase.updateColumn
import com.essntl.core.supabase.uploadImage
import com.essntl.core.utils.repository.Repository
import com.mohamedrejeb.calf.core.PlatformContext
import com.mohamedrejeb.calf.io.KmpFile
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.decodeFromJsonElement
import kotlinx.serialization.json.encodeToJsonElement
import org.koin.core.annotation.Single

@Single(binds = [ConfigurationRepository::class])
class ConfigurationRepositoryImpl internal constructor(
    private val supabase: ConfigurationSupabaseDataSource,
) : ConfigurationRepository, Repository() {

    override suspend fun getAllowedCurrencies(): Result<ConfigurationCurrencyModel?> = runCatching {
        val c = supabase.getAll {
            filter {
                eq("setting_key", "currencies")
            }
        }.firstOrNull() ?: return@runCatching null

        Json
            .decodeFromJsonElement<ConfigurationCurrencyDto>(c.settingValue)
            .toModel()
    }

    override suspend fun getEmails(): Result<ConfigurationEmailModel?> = runCatching {
        val c = supabase.getAll {
            filter {
                eq("setting_key", "purchase_order")
            }
        }.firstOrNull() ?: return@runCatching null

        Json
            .decodeFromJsonElement<ConfigurationEmailDto>(c.settingValue)
            .toModel()
    }


    override suspend fun updateAllowedCurrencies(
        configurationCurrency: ConfigurationCurrencyModel,
    ): Result<ConfigurationCurrencyModel> =
        runCatching {
            val requestMap = ConfigurationModel(
                settingKey = "currencies",
                settingValue = Json
                    .encodeToJsonElement<ConfigurationCurrencyDto>(configurationCurrency.toDto()),
            ).toRequestMap()

            val c = supabase.updateColumn(
                "setting_key",
                "currencies",
                requestMap,
            )

            Json.decodeFromJsonElement<ConfigurationCurrencyDto>(c.settingValue).toModel()
        }

    override suspend fun getLogo(): Result<ConfigurationLogoModel?> = runCatching {
        val c = supabase.getAll {
            filter { eq("setting_key", "logo") }
        }.firstOrNull() ?: return@runCatching null

        Json
            .decodeFromJsonElement<ConfigurationLogoDto>(c.settingValue)
            .toModel()
    }

    override suspend fun deleteLogo(url: String): Result<Unit> =
        runCatching {
            supabase.deleteImage(listOf(url))

            val requestMap = ConfigurationModel(
                settingKey = "logo",
                settingValue = Json
                    .encodeToJsonElement<ConfigurationLogoDto>(
                        ConfigurationLogoModel(url = null)
                            .toDto(),
                    ),
            ).toRequestMap()

            supabase.updateColumn(
                "setting_key",
                "logo",
                requestMap,
            )
        }

    override suspend fun updateLogo(
        file: KmpFile,
        folderName: String,
        bucketName: String,
        context: PlatformContext,
    ): Result<ConfigurationLogoModel> =
        runCatching {
            val uploadedImages =
                supabase.uploadImage(
                    bucketName = bucketName,
                    folderName = folderName,
                    context = context,
                    images = listOf(
                        SupabaseFile(
                            name = null,
                            file = file,
                        ),
                    ),
                )

            val requestMap = ConfigurationModel(
                settingKey = "logo",
                settingValue = Json
                    .encodeToJsonElement<ConfigurationLogoDto>(
                        ConfigurationLogoModel(url = uploadedImages.first())
                            .toDto(),
                    ),
            ).toRequestMap()

            val c = supabase.updateColumn(
                "setting_key",
                "logo",
                requestMap,
            )

            Json.decodeFromJsonElement<ConfigurationLogoDto>(c.settingValue).toModel()
        }
}